import React, { useEffect } from "react";
import { Box, Flex, Text, Tooltip as ChakraTooltip } from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import { categoryColors } from "@/features/my-dashboard/constants";
import {
  Bar,
  BarChart,
  Customized,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import MyDashboardCustomTooltip from "@/features/my-dashboard/components/chart/MyDashboardChartTooltip";
import MyDashboardChartXAxis from "@/features/my-dashboard/components/chart/MyDashboardChartXAxis";
import useGetMyDashboardProductByCategory from "@/features/my-dashboard/hooks/useGetMyDashboardProductByCategory";
import useIdentity from "@/features/ui/hooks/use-identity";
import { useSetRecoilState } from "recoil";
import { myDashboardActiveBrandStore } from "@/features/my-dashboard/stores/my-dashboard-active-brand-store";
import useGetMyDashboardLinkedUrlSiteInfo from "@/features/my-dashboard/hooks/useGetMyDashboardLinkedUrlSiteInfo";
import { formatDateToYMDE } from "@/features/my-dashboard/utils";
import MyDashboardChartCursor from "@/features/my-dashboard/components/chart/MyDashboardChartCursor";
import MyDashboardActiveCursor from "@/features/my-dashboard/components/chart/MyDashboardActiveCursor";
import { Trans } from "react-i18next";
import ko from "date-fns/locale/ko";
import { enUS } from "date-fns/locale";
import { format } from "date-fns";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

function MyDashboardChart() {
  const identity = useIdentity();
  const setActiveBrand = useSetRecoilState(myDashboardActiveBrandStore);
  const { i18n, tSentence } = useI18nHelper();
  const isKor = i18n.language === "ko";

  const { myDashboardProductByCategory } = useGetMyDashboardProductByCategory(
    identity?.company?.id
  );

  const categoryKeys = Array.from(
    new Set(
      myDashboardProductByCategory.flatMap((brand) =>
        brand.categories.map((c) => c.category)
      )
    )
  );

  const chartData = myDashboardProductByCategory.map((item) => {
    const newObj: { [key: string]: number } = {};
    item.categories.forEach((category) => {
      newObj[category.category] = category.count;
    });
    return {
      ...item,
      ...newObj,
    };
  });

  const barKeys = [...categoryKeys].reverse();

  const { linkedUrlSiteInfo } = useGetMyDashboardLinkedUrlSiteInfo(
    identity?.company?.id
  );

  useEffect(() => {
    if (!myDashboardProductByCategory) return;
    const firstItem = myDashboardProductByCategory.at(0);

    setActiveBrand({
      brandId: firstItem?.brand_id,
      brandName: firstItem?.brand_name,
    });
  }, [myDashboardProductByCategory, setActiveBrand]);

  return (
    <>
      <Flex justifyContent={"space-between"}>
        <Flex gap={"2px"} alignItems={"center"}>
          <Text
            textStyle={"body-lg-bold"}
            color={"#444444"}
            whiteSpace={"nowrap"}
          >
            {tSentence("my_dash:chart_title")}
          </Text>
          <ChakraTooltip
            label={
              <Text textStyle={"body-sm-medium"}>
                <Trans
                  i18nKey={"my_dash:chart_tooltip"}
                  values={{
                    date: formatDateToYMDE(
                      linkedUrlSiteInfo?.crawled_at,
                      isKor
                    ),
                  }}
                  components={{
                    br: <br />,
                  }}
                />
                {/*<Text>*업데이트 일자 : {}</Text>*/}
                {/*<Text>*/}
                {/*  본 데이터는 연동한 사이트의 상품, 브랜드, 카테고리 등의*/}
                {/*  데이터를 기반하여 제공됩니다.(월 1회 업데이트)*/}
                {/*</Text>*/}
              </Text>
            }
            hasArrow={true}
            placement={"top"}
            openDelay={500}
          >
            <Box>
              <SVG
                src={"/icons-v1_1/info-circle.svg"}
                width={20}
                height={20}
                color={"#8F8F8C"}
              />
            </Box>
          </ChakraTooltip>
        </Flex>
        <Flex gap={"12px"}>
          {categoryKeys.map((item, index) => (
            <Flex
              gap={"6px"}
              alignItems={"center"}
              key={item}
              whiteSpace={"nowrap"}
            >
              <SVG
                src={"/icons-v1_1/dot.svg"}
                width={8}
                height={8}
                color={categoryColors[index]}
                style={{
                  transform: "scale(6) translateY(0.2px)",
                }}
              />
              <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
                {item}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Box
        height={"100%"}
        sx={{
          "& .recharts-bar-rectangle": {
            cursor: "pointer",
          },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 16, right: 16, left: 0, bottom: 32 }}
            barSize={32}
            // style={{
            //   cursor: "pointer",
            // }}
            onClick={(state) => {
              if (!state.activePayload) return;
              setActiveBrand({
                brandId: state.activePayload?.at(0).payload.brand_id,
                brandName: state.activeLabel,
              });
            }}
          >
            <Customized component={<MyDashboardActiveCursor />} />
            {/* 아래에 순위만 숫자로 표시 */}
            <XAxis
              dataKey="brand_name"
              tickLine={false}
              stroke={"#D4D4D1"}
              tick={<MyDashboardChartXAxis />}
            />
            <Tooltip
              itemSorter={(item) => categoryKeys.indexOf(item.name as string)}
              content={MyDashboardCustomTooltip}
              cursor={<MyDashboardChartCursor />}
            />
            {/* 같은 stackId("score") 를 주면 한 막대 안에 위로 쌓인다 */}
            {barKeys.map((category, index) => (
              <Bar
                key={category}
                dataKey={category}
                stackId="score"
                fill={categoryColors[categoryKeys.indexOf(category)]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
}

export default MyDashboardChart;
